import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useProducts } from "../lib/useProducts";
import { Truck, ShieldCheck, FlaskConical, Plus, Minus, ShoppingCart, FileText, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";
import TrustBar from "../components/TrustBar";
import StickyMobileCart from "../components/StickyMobileCart";
import SEO from "../components/SEO";

const ProductDetail = () => {
  const { id } = useParams();
  const { items: PRODUCTS, loading } = useProducts();
  const product = PRODUCTS.find((p) => p.id === id || p.sku === id);
  const [variantIdx, setVariantIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [activeView, setActiveView] = useState("bottle");
  const { addItem } = useCart();

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-sm text-gray-500">Loading…</div>;
  if (!product) return <Navigate to="/shop" replace />;

  const variant = product.variants[variantIdx];
  const compare = variant.compareAt;
  const discount = compare && compare > variant.price ? Math.round(((compare - variant.price) / compare) * 100) : 0;
  const related = PRODUCTS.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);
  const infoCardUrl = product.infoCardImage || null;
  // If the product has per-variant images (e.g. RT-10 vs RT-20), swap the bottle image
  // when the customer picks a different size. Otherwise fall back to product.image.
  const bottleImage =
    (product.variantImages && product.variantImages[variant.size]) || product.image;
  const mainImage = activeView === "card" && infoCardUrl ? infoCardUrl : bottleImage;

  // Display name for category slugs.
  const CATEGORIES_LOOKUP = {
    peptides: "Research Peptides",
    blends: "Research Blends",
    supplies: "Research Supplies",
  };
  const categoryLabel = CATEGORIES_LOOKUP[product.category] || "Catalog";

  const onAdd = () => {
    addItem(product, variant, qty);
    toast.success(`${product.name} (${variant.size}) × ${qty} added to cart`);
  };

  // JSON-LD Product schema for Google Shopping rich results.
  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    sku: product.sku,
    description: product.description || product.short,
    image: [product.image, ...(product.infoCardImage ? [product.infoCardImage] : [])].filter(Boolean),
    brand: { "@type": "Brand", name: "Certified Wellness" },
    category: "Laboratory Research Chemicals",
    offers: product.variants.map((v) => ({
      "@type": "Offer",
      sku: `${product.sku}-${v.size}`,
      name: `${product.name} ${v.size}`,
      price: v.price.toFixed(2),
      priceCurrency: "USD",
      availability: v.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    })),
    ...(product.casNumber && {
      additionalProperty: [
        { "@type": "PropertyValue", name: "CAS Number", value: product.casNumber },
        ...(product.molecularFormula ? [{ "@type": "PropertyValue", name: "Molecular Formula", value: product.molecularFormula }] : []),
        ...(product.molecularWeight ? [{ "@type": "PropertyValue", name: "Molecular Weight", value: product.molecularWeight }] : []),
      ],
    }),
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-10 py-12">
      <SEO
        title={`${product.name} — ${product.short || "Research Compound"}`}
        description={product.description || `${product.name} — research-grade compound. HPLC-tested, COA on file. For laboratory research use only.`}
        canonical={`/product/${product.sku}`}
        image={product.image}
        type="product"
        structuredData={productStructuredData}
      />
      <nav className="text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-5 flex items-center flex-wrap">
        <Link to="/" className="hover:text-[#0a0a0a]">Home</Link>
        <ChevronRight className="h-3 w-3 mx-1" />
        <Link to="/shop" className="hover:text-[#0a0a0a]">Shop</Link>
        <ChevronRight className="h-3 w-3 mx-1" />
        <Link to={`/shop/${product.category}`} className="hover:text-[#0a0a0a]">{categoryLabel}</Link>
        <ChevronRight className="h-3 w-3 mx-1" />
        <span className="text-[#0a0a0a] font-semibold">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="relative aspect-square border border-gray-100 bg-white overflow-hidden">
            {discount > 0 && (
              <div className="absolute top-4 left-4 z-10 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#b91c1c]">
                — {discount}% off
              </div>
            )}
            {product.badge && (
              <div className="absolute top-4 right-4 z-10 text-[10px] font-semibold tracking-[0.2em] uppercase text-gray-500">
                {product.badge}
              </div>
            )}
            <img
              key={mainImage}
              src={mainImage}
              alt={product.name}
              className={`w-full h-full transition-opacity duration-300 ${activeView === "card" ? "object-contain p-2" : "object-contain p-10"}`}
            />
            {infoCardUrl && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-1 bg-white/95 backdrop-blur border border-gray-200 px-1 py-1 rounded-full shadow-sm">
                <button
                  onClick={() => setActiveView("bottle")}
                  className={`text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full transition-colors ${activeView === "bottle" ? "bg-[#0a0a0a] text-white" : "text-gray-600 hover:text-[#0a0a0a]"}`}
                >
                  Bottle
                </button>
                <button
                  onClick={() => setActiveView("card")}
                  className={`text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full transition-colors ${activeView === "card" ? "bg-[#0a0a0a] text-white" : "text-gray-600 hover:text-[#0a0a0a]"}`}
                >
                  Research Card
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button
              onClick={() => setActiveView("bottle")}
              className={`aspect-square border bg-white overflow-hidden transition-colors ${activeView === "bottle" ? "border-[#0a0a0a]" : "border-gray-200 hover:border-gray-400"}`}
            >
              <img src={product.image} alt="" className="w-full h-full object-contain p-3" />
            </button>
            {infoCardUrl ? (
              <button
                onClick={() => setActiveView("card")}
                className={`aspect-square border bg-white overflow-hidden transition-colors ${activeView === "card" ? "border-[#0a0a0a]" : "border-gray-200 hover:border-gray-400"}`}
              >
                <img src={infoCardUrl} alt="" className="w-full h-full object-contain p-1" />
              </button>
            ) : (
              <div className="aspect-square border border-dashed border-gray-200 bg-[#fafbfc] flex items-center justify-center">
                <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Card coming soon</span>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="h-px w-8 bg-[#b91c1c]" />
            <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">In Stock · Ships in 24h</span>
            <span className="text-gray-300">|</span>
            <span className="text-[10px] tracking-[0.18em] uppercase text-gray-400">SKU: {product.sku}</span>
            {product.casNumber && (
              <>
                <span className="text-gray-300">|</span>
                <span
                  data-testid="product-cas-number"
                  className="text-[10px] tracking-[0.18em] uppercase text-gray-400 font-mono"
                >
                  CAS: <span className="text-[#0a0a0a]">{product.casNumber}</span>
                </span>
              </>
            )}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#0a0a0a] leading-tight tracking-tight">{product.name}</h1>
          <p className="text-gray-500 mt-2 font-light">{product.short}</p>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-3xl font-semibold text-[#0a0a0a]">${variant.price.toFixed(2)}</span>
            {compare && compare > variant.price && (
              <>
                <span className="text-lg text-gray-400 line-through">${compare.toFixed(2)}</span>
                <span className="text-sm font-semibold tracking-[0.18em] uppercase text-[#b91c1c]">Save ${(compare - variant.price).toFixed(2)}</span>
              </>
            )}
          </div>

          {product.variants.length > 1 && (
            <div className="mt-7">
              <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#b91c1c] mb-3">Size</div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v, idx) => (
                  <button
                    key={v.size}
                    onClick={() => setVariantIdx(idx)}
                    className={`px-5 py-2.5 border text-[12px] font-semibold uppercase tracking-[0.15em] transition-colors ${
                      idx === variantIdx ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-gray-300 hover:border-[#0a0a0a] text-gray-700"
                    }`}
                  >
                    {v.size}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-7 flex flex-wrap items-stretch gap-3">
            <div className="inline-flex items-center border border-gray-300">
              <button className="px-3 h-12 hover:bg-gray-100" onClick={() => setQty((q) => Math.max(1, q - 1))}>
                <Minus className="h-4 w-4" />
              </button>
              <span className="px-5 font-semibold text-[#0a0a0a]">{qty}</span>
              <button className="px-3 h-12 hover:bg-gray-100" onClick={() => setQty((q) => q + 1)}>
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button onClick={onAdd} size="lg" className="flex-1 btn-red rounded-none h-12 text-[12px] font-semibold uppercase tracking-[0.18em]">
              <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart
            </Button>
          </div>

          {product.coaUrl ? (
            <a href={product.coaUrl} target="_blank" rel="noopener noreferrer" className="block mt-3" data-testid="coa-download-link">
              <Button variant="outline" className="w-full h-12 rounded-none border-[#0a0a0a] text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white text-[12px] font-semibold uppercase tracking-[0.18em]">
                <FileText className="h-4 w-4 mr-2" /> Download COA / Lab Report
              </Button>
            </a>
          ) : (
            <Button variant="outline" disabled className="w-full h-12 mt-3 rounded-none border-gray-300 text-gray-500 bg-[#fafbfc] cursor-not-allowed disabled:opacity-100 text-[12px] font-semibold uppercase tracking-[0.18em]" data-testid="coa-coming-soon-badge">
              <FileText className="h-4 w-4 mr-2" /> COA — Coming Soon
            </Button>
          )}

          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/spec-sheet.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="block mt-2"
            data-testid="spec-sheet-download-link"
          >
            <Button variant="outline" className="w-full h-12 rounded-none border-gray-300 text-gray-700 hover:border-[#0a0a0a] hover:text-[#0a0a0a] text-[12px] font-semibold uppercase tracking-[0.18em]">
              <FileText className="h-4 w-4 mr-2" /> Download Spec Sheet (PDF)
            </Button>
          </a>

          <div className="mt-7 grid grid-cols-3 gap-px bg-gray-200">
            {[
              { icon: ShieldCheck, t: "Lab Tested" },
              { icon: FlaskConical, t: "Made in USA" },
              { icon: Truck, t: "Free $200+" },
            ].map(({ icon: Icon, t }) => (
              <div key={t} className="bg-white py-4 px-2 text-center">
                <Icon className="h-4 w-4 text-[#b91c1c] mx-auto mb-1.5" />
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-600">{t}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 border border-gray-200 bg-[#fafbfc] text-[12px] text-gray-600 leading-relaxed">
            <strong className="text-[#0a0a0a] tracking-[0.1em]">FOR RESEARCH USE ONLY.</strong> This product is not intended for human or animal consumption. Must be 21+ to purchase.
          </div>
        </div>
      </div>

      <div className="mt-16">
        <Tabs defaultValue="description">
          <TabsList className="bg-transparent border-b border-gray-200 rounded-none p-0 h-auto w-full justify-start overflow-x-auto">
            <TabsTrigger value="description" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#b91c1c] data-[state=active]:text-[#0a0a0a] rounded-none px-5 py-3 font-semibold uppercase text-[11px] tracking-[0.18em]">Description</TabsTrigger>
            <TabsTrigger value="quality" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#b91c1c] data-[state=active]:text-[#0a0a0a] rounded-none px-5 py-3 font-semibold uppercase text-[11px] tracking-[0.18em]">Quality</TabsTrigger>
            {infoCardUrl && <TabsTrigger value="research-card" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#b91c1c] data-[state=active]:text-[#0a0a0a] rounded-none px-5 py-3 font-semibold uppercase text-[11px] tracking-[0.18em]">Research Card</TabsTrigger>}
            <TabsTrigger value="shipping" className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#b91c1c] data-[state=active]:text-[#0a0a0a] rounded-none px-5 py-3 font-semibold uppercase text-[11px] tracking-[0.18em]">Shipping</TabsTrigger>
          </TabsList>
          <TabsContent value="description" className="py-8 text-gray-700 leading-relaxed max-w-3xl font-light">
            <p>{product.description}</p>
          </TabsContent>
          <TabsContent value="quality" className="py-8">
            {product.casNumber && (
              <div className="mb-6 max-w-2xl border border-gray-200 bg-[#fafbfc] px-5 py-4">
                <div className="grid grid-cols-[120px_1fr] gap-y-1 text-[12px]">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 self-center">CAS Number</span>
                  <span data-testid="product-cas-number-quality" className="font-mono text-[#0a0a0a]">{product.casNumber}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 self-center">Purity</span>
                  <span className="text-[#0a0a0a]">&gt; 99% (HPLC verified)</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 self-center">Form</span>
                  <span className="text-[#0a0a0a]">Lyophilized powder</span>
                </div>
              </div>
            )}
            <ul className="space-y-3 max-w-2xl">
              {product.benefits.map((b) => (
                <li key={b} className="flex items-center gap-3 text-gray-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#b91c1c]" /> {b}
                </li>
              ))}
            </ul>
          </TabsContent>
          {infoCardUrl && (
            <TabsContent value="research-card" className="py-8">
              <div className="max-w-3xl mx-auto bg-white border border-gray-200">
                <img src={infoCardUrl} alt={`${product.name} research card`} className="w-full h-auto" />
              </div>
            </TabsContent>
          )}
          <TabsContent value="shipping" className="py-8 text-gray-700 max-w-3xl space-y-3 font-light">
            <p>Orders ship within 24 hours from our American facility, Monday–Friday.</p>
            <p>Free U.S. shipping on orders over $200. International shipping available.</p>
            <p>All shipments are tracked and delivered discreetly with temperature control.</p>
          </TabsContent>
        </Tabs>
      </div>

      {related.length > 0 && (
        <section className="mt-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-8 bg-[#b91c1c]" />
            <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Related</span>
          </div>
          <h2 className="font-display text-3xl font-semibold text-[#0a0a0a] mb-8 tracking-tight">You may also research</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      <section className="mt-20 pt-12 border-t border-gray-100">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <div className="h-px w-8 bg-[#b91c1c]" />
          <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Why Researchers Choose Us</span>
          <div className="h-px w-8 bg-[#b91c1c]" />
        </div>
        <TrustBar />
      </section>

      <StickyMobileCart price={variant.price} onAdd={onAdd} />
    </div>
  );
};

export default ProductDetail;
